import { createButton } from '@gluestack-ui/core/button/creator'
import { UIIcon } from '@gluestack-ui/core/icon/creator'
import { withStyleContext } from '@gluestack-ui/utils/nativewind-utils'
import { cssInterop } from 'nativewind'
import React, { forwardRef, type ReactNode } from 'react'
import { ActivityIndicator, Pressable, Text, View } from 'react-native'
import { twMerge } from 'tailwind-merge'

import { Typography } from '../../typography/Typography'
import { variantForStyleId } from '../../typography/variants'
import { buttonContract, type ContractVariant } from './button.contract'

const Root = withStyleContext(Pressable, 'BUTTON')

// Spinner and Icon are part of gluestack's contract even though the Figma Button
// has neither: it composes them itself, so they have to be supplied.
const UIButton = createButton({ Root, Text, Group: View, Spinner: ActivityIndicator, Icon: UIIcon })

cssInterop(UIButton, { className: 'style' })

export const BUTTON_SIZES = buttonContract.props.size.values
export const BUTTON_TYPES = buttonContract.props.type.values

export type ButtonSize = (typeof BUTTON_SIZES)[number]
export type ButtonType = (typeof BUTTON_TYPES)[number]

export const LABEL_VARIANT = variantForStyleId(buttonContract.textStyleIds[0])

export const buttonVariantKey = (size: ButtonSize, type: ButtonType): string =>
    `size=${size}, type=${type}`

export const resolveButton = (size: ButtonSize, type: ButtonType) => {
    const key = buttonVariantKey(size, type)
    const variant = (buttonContract.variants as Record<string, ContractVariant>)[key]
    if (!variant) {
        throw new Error(`aos-ds Button: the Figma contract has no "${key}"`)
    }
    return variant
}

export type ButtonProps = Omit<
    React.ComponentProps<typeof UIButton>,
    'style' | 'children' | 'size'
> & {
    size?: ButtonSize
    type?: ButtonType
    iconLeft?: ReactNode
    iconRight?: ReactNode
    className?: string
    children?: ReactNode
}

type ButtonRef = React.ComponentRef<typeof UIButton>

export const Button = forwardRef<ButtonRef, ButtonProps>(
    (
        {
            size = 'small',
            type = 'primary',
            iconLeft,
            iconRight,
            disabled = false,
            className,
            children,
            ...rest
        },
        ref,
    ) => {
        const variant = resolveButton(size, type)
        return (
            <UIButton
                ref={ref}
                disabled={disabled}
                className={twMerge(
                    'flex-row items-center justify-center',
                    variant.container,
                    disabled ? variant.disabled : undefined,
                    className,
                )}
                {...rest}
            >
                {iconLeft}
                {children === undefined ? null : (
                    <View className={variant.parts['text-wrapper']}>
                        <Typography variant={LABEL_VARIANT} className={variant.label}>
                            {children}
                        </Typography>
                    </View>
                )}
                {iconRight}
            </UIButton>
        )
    },
)

Button.displayName = 'Button'
